import { POPULATION_TYPE } from "@/common/models";
import * as entities_handler from "../entities";
import { Ref, computed, ref, watch } from "@vue/composition-api";
import { Subject, InvEntityType } from "./model";
import { UseLoading } from "@/common/composition/loading";
import { trust_state } from "./entity-trust.state";
import { state as investigations } from "@/views/investigation/data/state";
import { insights_counters_state } from "./insight-counters.state";
import { info_types_counters } from "./infotype-countets.state";
import { entity_counters } from "./entity-counters.state";
import { TimeFrameOption } from "@/common/state/models";
import { SharedState } from "@/common/state";
import { not_available } from "./not-available.state";
import { emptyFn, onNewValueOnly } from "@/fp";
import { EmployeesHelper } from "@/common/entities/helpers";
import { UseModal } from "@/components/root/modal";
import SaveInvestigationModal from "../SaveInvestigationModal.vue";
import { cluster_state } from "./cluster.state";
import { EventBus } from "@/eventbus";
import { UseDropdownItems } from "@/common/composition/dropdown-items";
import { state as data_sources_state } from "@/views/customize/data-sources/data/state";
import { TimeAxisType } from "@/gql/types";
import * as folders_api from "../entities/folders";
import { period_has_data, previous_period } from "./previous-period";
type TimeFrameSelection = {
  id: string;
  name: string;
  type: TimeAxisType;
  value: string;
};

/** the period the Activities Summary compares against — see previous-period.ts */
const relative_time_frame = computed<TimeFrameOption>(() =>
  previous_period(
    SharedState.selectedTimeFrameOption.value,
    SharedState.timeFrameOptions.value
  )
);

/** false in the org's first month or week: one column, not two (task 25042) */
const has_previous_period = computed(() =>
  period_has_data(
    relative_time_frame.value,
    SharedState.timeFrameOptions.value,
    SharedState.basedOn.value && SharedState.basedOn.value.dateFrom
  )
);

const [
  from_time_frames,
  selected_from_time_frame,
  _set_selected_from_time_frame,
  init_time_frames,
] = UseDropdownItems<TimeFrameSelection>([]);

const set_selected_from_time_frame = async (time_frame: TimeFrameSelection) => {
  _set_selected_from_time_frame(time_frame.id);
  SharedState.setSelectedTimeFrameOption(
    SharedState.timeFrameOptions.value.find(
      (f) => f.displayName == time_frame.name
    )!
  );
};

const subjects: Ref<Record<string, Subject>> = ref({});

const add_subject = (subject: Subject) => {
  subjects.value = {
    ...subjects.value,
    [subject.id]: subject,
  };
  EventBus.emit(EventBus.GLOBAL_EVENTS.CLOSE_ALL_MODALS);
};
let prev_subject_id = "";
const change_subject = (subject: Subject) => {
  // making sure this subject exist
  if (!(subject.id in subjects.value)) {
    console.log("WOW !! where did you got this subject from?");
    return;
  }
  prev_subject_id = current_subject.value.id;
  init(subject);
};

const remove_subject = (subject: Subject) => {
  if (subject.id == current_subject.value.id) {
    if (subjects.value[prev_subject_id]) {
      change_subject(subjects.value[prev_subject_id]);
    } else {
      change_subject(Object.values(subjects.value)[0]);
    }
  }
  if (subjects.value[subject.id]) {
    subjects.value = Object.entries(subjects.value).reduce((acc, [id, sub]) => {
      if (id != subject.id) {
        acc[id] = sub;
      }
      return acc;
    }, {} as Record<string, Subject>);
  }
};

const current_subject: Ref<Subject> = ref({
  type: POPULATION_TYPE.NONE,
  id: "",
  name: "",
});

const set_current_subject = async (subject: Subject) => {
  current_subject.value = subject;
  if (current_subject.value.type === POPULATION_TYPE.EMPLOYEE) {
    const dep = await EmployeesHelper.get_single_employee_department(
      current_subject.value.id
    );
    current_subject.value.department = dep;
  }
  if (current_subject.value.type === POPULATION_TYPE.FOLDER) {
    const sub_folders_count_res = await folders_api.get_sub_items_count(
      subject.id
    );
    if (sub_folders_count_res.success) {
      current_subject.value.sub_folders_count =
        sub_folders_count_res.value.count;
    }
  }
  setTimeout(() => {
    EventBus.emit(
      "timeframe:change_vis",
      subject.type === POPULATION_TYPE.FILE
    );
  }, 500);
};

const is_watched_subject = computed(() =>
  investigations.has_investigation(current_subject.value.id)
);

const save_investigation = () => {
  UseModal().show(SaveInvestigationModal, {
    item: current_subject.value,
    mode: "save",
  });
};

const delete_investigation = async () => {
  const res = await UseModal().showSimple({
    title: "Remove from watchlist",
    desc: "Are you sure?",
    submitBtnText: "Remove",
  });
  if (res.type === "success") {
    investigations.deleteInvestigationBySubjectId(current_subject.value.id);
  }
};

const [is_loading_counters, set_loading_counters] = UseLoading(false).as_arr();

const populate_counters = async (subject: Subject) => {
  const time_frame = SharedState.selectedTimeFrameDisplayName.value;
  set_loading_counters(true);
  await insights_counters_state.load_counters(subject, time_frame);
  await info_types_counters.load_counters(subject, time_frame);
  await entity_counters.load_counters(subject, relative_time_frame.value);
  set_loading_counters(false);
};

const on_count_clicked = (type: InvEntityType) => {
  entities_handler.show_modal(current_subject.value, type);
};

const investigate = (new_subject: Subject) => {
  SharedState.ifIsPayingUser(
    () => {
      init(new_subject);
    },
    { premium_modal_key: "forensics-popup-2024" }
  );
};

const subs = {
  time_frame_sub: emptyFn(),
};

const watch_for_time_frame_changes = () => {
  subs.time_frame_sub = watch(
    SharedState.selectedTimeFrameDisplayName,
    onNewValueOnly<string>(
      (s1: string, s2: string) => s1 == s2 || !s2,
      (t1, t2) => {
        if (t1 !== t2 && t2) {
          populate_counters(current_subject.value);
        }
      }
    )
  );
};

let original_time_frame: string;
const load_time_frames = () => {
  const time_frames = data_sources_state.ext.get_in_motion_source_time_frames(
    "motion_365"
  );
  init_time_frames(
    time_frames.map((t) => {
      return {
        id: t.displayName,
        name: t.displayName,
        type: t.type,
        value: t.from,
      };
    })
  );
  const selected_frame = from_time_frames.value.find(
    (d) => d.id == SharedState.selectedTimeFrameOption.value.displayName
  )!;
  original_time_frame = selected_frame.id;
  set_selected_from_time_frame(selected_frame);
};

let is_init = false;
const init = async (subject: Subject, post_subject_added?: () => void) => {
  // force close all open modals
  load_time_frames();
  await set_current_subject(subject);
  add_subject(subject);
  post_subject_added && post_subject_added();
  if (subject.type === POPULATION_TYPE.FILE) {
    // await get_cluster_data(subject);
    await cluster_state.init_cluster_data(subject.id);
  } else {
    await populate_counters(subject);
    if (trust_state.is_trust_relevant_for_entity(subject)) {
      await trust_state.load_entity_trust(subject);
    }
  }

  if (!is_init) {
    watch_for_time_frame_changes();
  }
  is_init = true;
};

const destroy = () => {
  Object.values(subs).forEach((un_sub) => un_sub());
  subjects.value = {};
  // reset to original time frame so that results
  // in overview will not be effected
  SharedState.setSelectedTimeFrameOption(
    SharedState.timeFrameOptions.value.find(
      (f) => f.displayName == original_time_frame
    )!
  );
  is_init = false;
};

export const state = {
  // lc
  init,
  destroy,
  // time frame
  relative_time_frame,
  has_previous_period,
  from_time_frames,
  selected_from_time_frame,
  set_selected_from_time_frame,
  // subject
  current_subject,
  subjects,
  change_subject,
  remove_subject,
  is_watched_subject,
  // save, delete inv
  save_investigation,
  delete_investigation,
  // counters
  entity_counters: entity_counters.counters,
  insights_counters: insights_counters_state.counters,
  info_types_counters: info_types_counters.counters,
  is_loading_counters,
  on_count_clicked,
  // trust
  is_loading_entity_trust: trust_state.is_loading_entity_trust,
  entity_info_trust: trust_state.entity_info_trust,
  is_trust_relevant_for_entity: trust_state.is_trust_relevant_for_entity,
  // not_available
  not_available,
  // action
  investigate,
  cluster: cluster_state,
};
